import produce from 'immer';
import { assign, cloneDeep } from 'lodash-es';
import * as Redux from 'redux';

interface Window {
  devToolsExtension: any;
  __REDUX_DEVTOOLS_EXTENSION__?: any;
}

/* tslint:disable:no-switch-case-fall-through */
// Needed because tslint gets confused by a block scope on a case where it always returns within that block.

////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Note: To understand this code is to understand how Redux works. So it's really worth taking an hour or so and
// going through the Redux docs (http://redux.js.org/index.html) to get what's being done here.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

// The most basic actions are those which go get data from an API and then load it into our data model.
const FETCH_REBATES = 'FETCH_REBATES';
const LOAD_REBATES = 'LOAD_REBATES';

// Then we've got those associated with user actions on the page.
const ADD_REBATE = 'ADD_REBATE';
const UPDATE_REBATE_ID = 'UPDATE_REBATE_ID';
const UPDATE_REBATE = 'UPDATE_REBATE';
const REMOVE_REBATE = 'REMOVE_REBATE';
const SET_AUTHORIZATION = 'SET_AUTHORIZATION';

export default class RebatesModelService {
  rfpId;
  offerId;

  cpaId;

  initialState = {
    fetching: {
      rebates: false
    },

    rebateProxyAuthorization: false,
    rebates: []
  };

  store;

  ////////////////////////////////////////////////////////////////////////////////
  // Reducers - Again, it's worth reading through the Redux docs to understand why
  // these are structured as they are.
  ////////////////////////////////////////////////////////////////////////////////

  rebatesReducer(state: any, action: any) {
    if (!state) {
      state = cloneDeep(this.initialState);
    }

    switch (action.type) {
      case FETCH_REBATES:
        // Mark that we're in the process of fetching the rebates. Other code could use this to throw up a loading
        // indicator.
        return produce(state, (draft) => {
          draft.fetching.rebates = true;
        });
      case LOAD_REBATES:
        // Create a new state based upon the returned rebates information and mark that we're not fetching rebates
        // anymore.
        return produce(state, (draft) => {
          draft.fetching.rebates = false;

          draft.rebateProxyAuthorization = action.data.rebateProxyAuthorization;
          draft.rebates = cloneDeep(action.data.rebates);
        });
      case ADD_REBATE:
        return produce(state, (draft) => {
          draft.rebates.push(cloneDeep(action.data));
        });
      case UPDATE_REBATE_ID:
        // This is basically the same as a regular update except that the initial ID we're looking for is "Temporary".
        return produce(state, (draft) => {
          let rebateToUpdate = draft.rebates.find((rebate) => {
            return rebate.id === 'Temporary';
          });

          Object.assign(rebateToUpdate, action.data);
        });
      case UPDATE_REBATE:
        return produce(state, (draft) => {
          let rebateToUpdate = draft.rebates.find((rebate) => {
            return rebate.id === action.data.id;
          });

          Object.assign(rebateToUpdate, action.data);
        });
      case REMOVE_REBATE:
        return produce(state, (draft) => {
          draft.rebates = draft.rebates.filter((rebate) => {
            return rebate.id !== action.data;
          });
        });
      case SET_AUTHORIZATION:
        return produce(state, (draft) => {
          draft.rebateProxyAuthorization = action.data;
        });
      default:
        // Nothing changed, return the same state.
        return state;
    }
  }

  constructor(private api) {
    'ngInject';

    let boundReducer = this.rebatesReducer.bind(this);

    // Create a Redux store to manipulate the state of this view: http://redux.js.org/index.html
    this.store = Redux.createStore(
      boundReducer,
      null,
      (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
        (window as any).__REDUX_DEVTOOLS_EXTENSION__()
    );
  }

  rebatesCount() {
    return this.store.getState().rebates.length;
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Actions - These are what outside code will call to trigger model changes.
  // Outside code does not dispatch actions to the Redux store, it calls these
  // functions or reads values from the state.
  ////////////////////////////////////////////////////////////////////////////////
  getRebates(rfpId, offerId) {
    let loadRebates = this.api.rebates.getRebates(rfpId, offerId);

    // I'm storing these off so they don't have to be passed in each and every time we call a function on the model.
    // The assumption is that we'll continue working with the same RFP and offer until we do a new getRebates() call.
    this.rfpId = rfpId;
    this.offerId = offerId;

    this.fetchAndLoadRebates(loadRebates);
  }

  getRebatesByCpa(cpaId) {
    let loadRebates = this.api.rebates.getRebatesByCpa(cpaId);

    // I'm storing these off so they don't have to be passed in each and every time we call a function on the model.
    // The assumption is that we'll continue working with the same RFP and offer until we do a new getRebates() call.
    this.cpaId = cpaId;

    this.fetchAndLoadRebates(loadRebates);
  }

  fetchAndLoadRebates(loadRebates) {
    this.store.dispatch({ type: FETCH_REBATES });

    loadRebates.then((rebates) => {
      this.store.dispatch({ type: LOAD_REBATES, data: rebates });
    });
  }

  addRebate(rebate) {
    let addRebatePromise;

    if (this.rfpId) {
      // Make the API call to add the rebate.
      addRebatePromise = this.api.rebates.addRebate(
        this.rfpId,
        this.offerId,
        rebate
      );
    } else {
      addRebatePromise = this.api.rebates.addRebateByCpa(this.cpaId, rebate);
    }

    addRebatePromise.then((rebate) => {
      // Note: We will have to make a second dispatch when the results return
      // so we can add the rebate ID number onto the rebate in the local store.
      this.store.dispatch({ type: UPDATE_REBATE_ID, data: rebate });
    });

    // We add a temporary ID to the rebate so we can find it later to update it.
    // Then immediately dispatch an action to add the rebate to the local store.
    this.store.dispatch({
      type: ADD_REBATE,
      data: assign({}, rebate, { id: 'Temporary' })
    });
  }

  updateRebate(rebate) {
    if (this.rfpId) {
      // Make the API call to update the rebate.
      this.api.rebates.updateRebate(
        this.rfpId,
        this.offerId,
        rebate.id,
        rebate
      );
    } else {
      this.api.rebates.updateRebateByCpa(this.cpaId, rebate.id, rebate);
    }

    // Then immediately dispatch an action to change the details for teh rebate in the local store.
    this.store.dispatch({ type: UPDATE_REBATE, data: rebate });
  }

  removeRebate(rebateId) {
    if (this.rfpId) {
      // Make the API call to remove the rebate.
      this.api.rebates.removeRebate(this.rfpId, this.offerId, rebateId);
    } else {
      this.api.rebates.removeRebateByCpa(this.cpaId, rebateId);
    }

    // Then immediately dispatch an action to remove the rebate from the local store.
    this.store.dispatch({ type: REMOVE_REBATE, data: rebateId });
  }

  setProxyAuthorization(authorized) {
    if (this.rfpId) {
      // Make the API call to update the proxy authorization.
      this.api.rebates.updateRebateProxyAuthorization(
        this.rfpId,
        this.offerId,
        authorized
      );
    } else {
      this.api.rebates.updateRebateProxyAuthorizationByCpa(
        this.cpaId,
        authorized
      );
    }

    // Then immediately dispatch an action to update the local store.
    this.store.dispatch({ type: SET_AUTHORIZATION, data: authorized });
  }
}
